'use client';

import { useCallback } from 'react';
import { MixpanelTracking } from '@/services/mixpanel';
import { useAgentContext } from '../context/AgentContext';
import { useAgentWebSocket } from './useAgentWebSocket';
import { usePageContext } from './usePageContext';
import type { ChatMessage } from '../types/agent.types';

export function useAgentChat() {
  const { state, dispatch, addUserMessage } = useAgentContext();
  const { sendMessage: wsSendMessage, cancelResponse: wsCancelResponse } = useAgentWebSocket();
  const pageContext = usePageContext();

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim() || state.isStreaming) return;

      const userMessage: ChatMessage = {
        messageId: `user-${Date.now()}`,
        role: 'user',
        content: text.trim(),
        createdAt: new Date().toISOString(),
      };

      addUserMessage(userMessage);
      wsSendMessage(text.trim(), state.activeConversationId || undefined, pageContext);

      MixpanelTracking.getInstance().track('ai_assistant_prompt', {
        prompt: text.trim(),
        conversationId: state.activeConversationId || null,
        page: pageContext.page,
      });
    },
    [state.isStreaming, state.activeConversationId, addUserMessage, wsSendMessage, pageContext]
  );

  const cancelResponse = useCallback(() => {
    if (!state.activeConversationId || !state.isStreaming) return;
    wsCancelResponse(state.activeConversationId);
    // Optimistically stop the UI immediately — the backend cancel uses in-memory
    // abort signals which don't work across Lambda invocations, so the 'cancelled'
    // event from the backend may never arrive.
    dispatch({
      type: 'FINISH_STREAMING',
      payload: {},
    });
  }, [state.activeConversationId, state.isStreaming, wsCancelResponse, dispatch]);

  return {
    messages: state.messages,
    isStreaming: state.isStreaming,
    streamingContent: state.streamingContent,
    streamingToolCalls: state.streamingToolCalls,
    connectionStatus: state.connectionStatus,
    activeConversationId: state.activeConversationId,
    error: state.error,
    sendMessage,
    cancelResponse,
  };
}
